import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../Common/Navbar";
import "./ProfileSettings.css";

/* ==============================
   API BASE
============================== */
const API_BASE = (process.env.REACT_APP_API_BASE || "http://localhost:5000").replace(
  /\/+$/,
  ""
);

/* ==============================
   AUTH HELPERS
============================== */
const AUTH_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "isLoggedIn",
  "user",
  "name",
  "userName",
  "fullName",
  "username",
  "email",
  "userEmail",
  "user_email",
  "demoUserEmail",
];

const getToken = () =>
  (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("accessToken") ||
    ""
  ).trim();

const clearAuth = () => {
  AUTH_KEYS.forEach((key) => localStorage.removeItem(key));
  window.dispatchEvent(new Event("auth-state-changed"));
};

/* ==============================
   COMPONENT
============================== */
const SettingsPage = () => {
  const navigate = useNavigate();

  const [emailNotifications, setEmailNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [twoFactor, setTwoFactor] = useState(true);

  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  /* ==============================
     LOAD SETTINGS (SAFE)
  ============================== */
  useEffect(() => {
    try {
      const savedSettings = JSON.parse(localStorage.getItem("userSettings")) || {};

      setEmailNotifications(savedSettings.emailNotifications ?? true);
      setSmsNotifications(savedSettings.smsNotifications ?? false);
      setDarkMode(savedSettings.darkMode ?? false);
      setTwoFactor(savedSettings.twoFactor ?? true);
    } catch {
      // safe ignore
    }
  }, []);

  /* ==============================
     SAVE SETTINGS (PRO LEVEL)
  ============================== */
  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      setError("");
      setNotice("");

      const token = getToken();

      if (!token) {
        clearAuth();
        navigate("/signin", { replace: true });
        return;
      }

      const settingsData = {
        emailNotifications,
        smsNotifications,
        darkMode,
        twoFactor,
      };

      /* 🔥 OPTIONAL BACKEND CONNECT */
      try {
        await fetch(`${API_BASE}/api/settings`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(settingsData),
        });
      } catch {
        // fallback silently
      }

      /* LOCAL SAFE SAVE */
      localStorage.setItem("userSettings", JSON.stringify(settingsData));

      setNotice("Settings saved successfully.");
    } catch (err) {
      console.error("Settings save error:", err);
      setError("Failed to save settings.");
    } finally {
      setLoading(false);
    }
  };

  /* ==============================
     LOGOUT ALL DEVICES (SAFE)
  ============================== */
  const handleLogoutAll = () => {
    clearAuth();
    localStorage.removeItem("cart");
    localStorage.removeItem("userSettings");

    navigate("/signin", { replace: true });
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <>
      <Navbar />

      <section className="ps-page">
        <div className="ps-container">
          <div className="ps-topbar">
            <button
              type="button"
              className="ps-back-btn"
              onClick={handleBack}
              aria-label="Go back"
            >
              ← Back
            </button>
          </div>

          {error && <div className="auth-alert auth-error">{error}</div>}
          {notice && <div className="auth-alert auth-success">{notice}</div>}

          <div className="ps-header-card">
            <div className="ps-settings-icon">⚙️</div>

            <div className="ps-header-content">
              <span className="ps-badge">Account Settings</span>
              <h1>Settings</h1>
              <p>Control your preferences, privacy, and notification options.</p>
            </div>

            <div className="ps-header-action">
              <button
                type="button"
                className="ps-edit-btn"
                onClick={handleSaveSettings}
                disabled={loading}
              >
                {loading ? "Saving..." : "Save Settings"}
              </button>
            </div>
          </div>

          <div className="ps-grid">
            <div className="ps-card">
              <h3>Notifications</h3>

              <div className="ps-toggle-row">
                <div>
                  <strong>Email Notifications</strong>
                  <p>Receive important updates on your email.</p>
                </div>
                <label className="ps-switch">
                  <input
                    type="checkbox"
                    checked={emailNotifications}
                    onChange={() => setEmailNotifications((prev) => !prev)}
                  />
                  <span className="ps-slider"></span>
                </label>
              </div>

              <div className="ps-toggle-row">
                <div>
                  <strong>SMS Notifications</strong>
                  <p>Receive alerts and account notifications on phone.</p>
                </div>
                <label className="ps-switch">
                  <input
                    type="checkbox"
                    checked={smsNotifications}
                    onChange={() => setSmsNotifications((prev) => !prev)}
                  />
                  <span className="ps-slider"></span>
                </label>
              </div>
            </div>

            <div className="ps-card">
              <h3>Security Preferences</h3>

              <div className="ps-toggle-row">
                <div>
                  <strong>Two-Factor Authentication</strong>
                  <p>Add extra protection to your account login.</p>
                </div>
                <label className="ps-switch">
                  <input
                    type="checkbox"
                    checked={twoFactor}
                    onChange={() => setTwoFactor((prev) => !prev)}
                  />
                  <span className="ps-slider"></span>
                </label>
              </div>

              <div className="ps-toggle-row">
                <div>
                  <strong>Dark Mode</strong>
                  <p>Switch your dashboard appearance style.</p>
                </div>
                <label className="ps-switch">
                  <input
                    type="checkbox"
                    checked={darkMode}
                    onChange={() => setDarkMode((prev) => !prev)}
                  />
                  <span className="ps-slider"></span>
                </label>
              </div>
            </div>
          </div>

          <div className="ps-card">
            <h3>Danger Zone</h3>
            <div className="ps-danger-row">
              <div>
                <strong>Logout from all devices</strong>
                <p>Sign out your account from every active session securely.</p>
              </div>

              <button
                type="button"
                className="ps-danger-btn"
                onClick={handleLogoutAll}
              >
                Logout All
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SettingsPage;